/* eslint-disable */
// Demo component showing Event API usage
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Badge,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useNotifications, useEventAPIStatus, useNotificationTester } from '../hooks/useEventAPI';
import { useAuth } from '../contexts/AuthContext';

const EventAPIDemo = () => {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, clearAll, addTestNotification } = useNotifications();
  const { testSubscription } = useNotificationTester();
  const status = useEventAPIStatus();
  
  const [testMessage, setTestMessage] = useState('');
  const [testType, setTestType] = useState('INFO');

  const handleSendTestNotification = () => {
    if (!user?.id || !testMessage.trim()) return;

    console.log('🧪 Testing subscription with message:', testMessage);

    try {
      const mockNotification = {
        id: `test-${Date.now()}`,
        userID: user.id,
        type: testType,
        message: testMessage,
        isRead: false,
        metadata: JSON.stringify({ source: 'demo', timestamp: new Date().toISOString() }),
        createdAt: new Date().toISOString()
      };
      
      // Add test notification to state
      addTestNotification(mockNotification);
      console.log('✅ Test notification added:', mockNotification);
      setTestMessage('');
    } catch (error) {
      console.error('❌ Failed to test subscription:', error);
      alert(`Failed to test subscription: ${error.message}`);
    }
  };

  const handleRunPerformanceTest = () => {
    if (!user?.id) return;

    console.log('⚡ Running subscription performance test...');
    const startTime = Date.now();

    try {
      // Test 5 notifications rapidly
      for (let i = 1; i <= 5; i++) {
        setTimeout(() => {
          const mockNotification = {
            id: `perf-test-${i}-${Date.now()}`,
            userID: user.id,
            type: 'INFO',
            message: `Performance test notification #${i}`,
            isRead: false,
            metadata: JSON.stringify({ testId: i, startTime }),
            createdAt: new Date().toISOString()
          };
          
          addTestNotification(mockNotification);
          console.log(`✅ Performance test notification #${i} added:`, mockNotification);
        }, i * 200);
      }
    } catch (error) {
      console.error('Performance test failed:', error);
    }
  };

  if (!user) {
    return (
      <Alert severity="warning">
        Please sign in to test the Event API features.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        🚀 Event API Demo
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Real-time notifications and chat with 75-85% cost reduction vs polling
      </Typography>

      <Grid container spacing={3}>
        {/* Status Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SpeedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">API Status</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={status.isHealthy ? 'Healthy' : 'Unhealthy'}
                  color={status.isHealthy ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                Active Subscriptions: {status.activeSubscriptions}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Last Update: {status.lastUpdate?.toLocaleTimeString() || 'Never'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Test Controls */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Controls
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Test Notification Message"
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {['INFO', 'SUCCESS', 'WARNING', 'ERROR'].map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      onClick={() => setTestType(type)}
                      color={testType === type ? 'primary' : 'default'}
                      variant={testType === type ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSendTestNotification}
                    disabled={!testMessage.trim()}
                  >
                    Test Subscription
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<SpeedIcon />}
                    onClick={handleRunPerformanceTest}
                  >
                    Test Multiple
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Real-time Notifications */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon color="primary" />
                  </Badge>
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Real-time Notifications
                  </Typography>
                </Box>
                
                <Button
                  size="small"
                  onClick={clearAll}
                  disabled={notifications.length === 0}
                >
                  Clear All
                </Button>
              </Box>

              {notifications.length === 0 ? (
                <Typography color="text.secondary">
                  No notifications yet. Send a test notification to see real-time updates!
                </Typography>
              ) : (
                <List>
                  {notifications.slice(0, 10).map((notification, index) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        sx={{
                          bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={notification.type}
                                size="small"
                                color={
                                  notification.type === 'ERROR' ? 'error' :
                                  notification.type === 'SUCCESS' ? 'success' :
                                  notification.type === 'WARNING' ? 'warning' : 'info'
                                }
                              />
                              <Typography variant="body2">
                                {notification.message}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {new Date(notification.createdAt).toLocaleString()}
                            </Typography>
                          }
                        />
                        
                        {!notification.isRead && (
                          <Button
                            size="small"
                            onClick={() => markAsRead(notification.id)}
                          >
                            Mark Read
                          </Button>
                        )}
                      </ListItem>
                      {index < notifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 Testing Instructions
              </Typography>
              
              <Typography variant="body2" paragraph>
                1. <strong>Test Subscription:</strong> Type a message and click "Test Subscription" to simulate a notification
              </Typography>
              
              <Typography variant="body2" paragraph>
                2. <strong>Test Multiple:</strong> Click "Test Multiple" to simulate 5 rapid notifications
              </Typography>
              
              <Typography variant="body2" paragraph>
                3. <strong>Browser Console:</strong> Open browser console to see detailed logs and use <code>testEventAPI.testNotifications('{user.id}')</code>
              </Typography>
              
              <Typography variant="body2" paragraph>
                4. <strong>Cost Monitoring:</strong> Check AWS AppSync console for subscription metrics and cost savings
              </Typography>
              
              <Alert severity="success" sx={{ mt: 2 }}>
                <strong>Expected Benefits:</strong> 75-85% cost reduction vs polling, instant delivery vs 2-minute delays
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EventAPIDemo;